import { useEffect } from 'react';
import { getMyWelfareRoles } from '~/api/welfareAdmins';
import { useConfig } from '~/contexts/ConfigContext';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import {
    clearWelfareRoles,
    setWelfareRoles,
} from '~/redux/modules/welfareRoles';

// Fetches the current administrator's Welfare roles (/me) once per
// authenticated session and on hard refresh. This is the source of truth for
// every Welfare permission gate in the app. Mount once (in App).
const useGetWelfareRoles = () => {
    const dispatch = useAppDispatch();
    const { id } = useAppSelector(({ profile }) => profile);
    const { isAuthed } = useAppSelector(({ authentication }) => authentication);
    const { config } = useConfig();

    const onGetRoles = async () => {
        if (!id) return;

        try {
            const { response } = await getMyWelfareRoles(id);

            dispatch(
                setWelfareRoles({
                    is_super_admin: response?.is_super_admin,
                    permissions: response?.permissions,
                })
            );
        } catch (error) {
            // A missing authorization record legitimately returns an error for
            // non-admin members. Treat any failure as zero permissions so the
            // app stays usable read-only; do not toast.
            dispatch(setWelfareRoles({ is_super_admin: false }));
        }
    };

    useEffect(() => {
        if (!isAuthed || !id || !config?.API_APP_ID) return;

        // Never act on stale persisted roles after a refresh.
        dispatch(clearWelfareRoles());
        onGetRoles();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthed, id, config?.API_APP_ID]);

    return { onGetRoles };
};

export default useGetWelfareRoles;
